import { useEffect, useRef, useState, type RefObject } from 'react';

export type GazeAxis = -1 | 0 | 1;

export interface GazeState {
  x: GazeAxis;
  y: GazeAxis;
}

export interface UseGazeResult extends GazeState {
  ref: RefObject<HTMLPreElement | null>;
}

export const GAZE_PROBABILITY = 0.3;
export const GAZE_COOLDOWN_MS = 150;

function axisSign(delta: number): GazeAxis {
  if (delta > 0) return 1;
  if (delta < 0) return -1;
  return 0;
}

/**
 * Attach `result.ref` to the face element. On pointermove, 30% of events
 * pass the random gate and (respecting a cooldown) nudge the face one
 * character-space toward the cursor.
 */
export function useGaze(reducedMotion = false): UseGazeResult {
  const ref = useRef<HTMLPreElement | null>(null);
  const [offset, setOffset] = useState<GazeState>({ x: 0, y: 0 });
  const lastUpdate = useRef(0);

  useEffect(() => {
    if (reducedMotion) return;

    const handler = (e: PointerEvent) => {
      if (Math.random() >= GAZE_PROBABILITY) return;
      const now = performance.now();
      if (now - lastUpdate.current < GAZE_COOLDOWN_MS) return;
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      lastUpdate.current = now;
      setOffset({
        x: axisSign(e.clientX - cx),
        y: axisSign(e.clientY - cy),
      });
    };

    window.addEventListener('pointermove', handler);
    return () => {
      window.removeEventListener('pointermove', handler);
    };
  }, [reducedMotion]);

  return { ...offset, ref };
}
